import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { PageLayout } from '../../../components/organisms/PageLayout';
import { api } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import '../../../styles/admin.css';

interface Stats {
  users: { total: number; new_last_30d: number; banned: number };
  content: { cvs: number; jobs: number; contacts: number };
  subscriptions: { plan_name: string; plan_code: string; count: number }[];
  ai: { total_calls: number; total_cost_usd: number };
}

const AdminOverview: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyticsTriggering, setAnalyticsTriggering] = useState(false);
  const [analyticsMsg, setAnalyticsMsg] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Admin | Overview';
    api.get<Stats>(API_ENDPOINTS.admin.stats)
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleTriggerAnalytics = async () => {
    setAnalyticsTriggering(true);
    setAnalyticsMsg(null);
    try {
      const res = await api.post<{ status: string; task_id: string }>(API_ENDPOINTS.admin.triggerAnalytics, {});
      setAnalyticsMsg(`Triggered — task ${res.task_id}`);
    } catch {
      setAnalyticsMsg('Failed to trigger');
    } finally {
      setAnalyticsTriggering(false);
    }
  };

  return (
    <PageLayout breadcrumbItems={[{ label: 'Admin' }, { label: 'Overview' }]}>
      <nav className="admin-nav">
        <NavLink to="/admin" end className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Overview</NavLink>
        <NavLink to="/admin/users" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Users</NavLink>
        <NavLink to="/admin/ai-usage" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>AI Usage</NavLink>
        <NavLink to="/admin/ai-settings" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>AI Settings</NavLink>
        <NavLink to="/admin/plans" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Plans</NavLink>
        <NavLink to="/admin/blog" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Blog</NavLink>
      </nav>

      {loading && <div className="admin-loading">Loading…</div>}

      {stats && (
        <>
          <div className="admin-stats">
            <div className="admin-stat-card">
              <div className="admin-stat-card__label">Total Users</div>
              <div className="admin-stat-card__value">{stats.users.total}</div>
              <div className="admin-stat-card__sub">+{stats.users.new_last_30d} last 30d</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-card__label">Banned</div>
              <div className="admin-stat-card__value">{stats.users.banned}</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-card__label">CVs</div>
              <div className="admin-stat-card__value">{stats.content.cvs}</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-card__label">Jobs</div>
              <div className="admin-stat-card__value">{stats.content.jobs}</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-card__label">Contacts</div>
              <div className="admin-stat-card__value">{stats.content.contacts}</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-card__label">AI Calls</div>
              <div className="admin-stat-card__value">{stats.ai.total_calls}</div>
              <div className="admin-stat-card__sub">${stats.ai.total_cost_usd.toFixed(4)} total</div>
            </div>
          </div>

          <p className="admin-section-title">Actions</p>
          <div style={{ marginBottom: 32, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              className="admin-btn admin-btn--primary"
              onClick={handleTriggerAnalytics}
              disabled={analyticsTriggering}
            >
              {analyticsTriggering ? 'Triggering…' : 'Recompute All Analytics'}
            </button>
            {analyticsMsg && (
              <span style={{ fontSize: 12, color: '#6b7280' }}>{analyticsMsg}</span>
            )}
            <button
              className="admin-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('show-upsell-modal'))}
            >
              Preview Paywall Popup
            </button>
          </div>

          <p className="admin-section-title">Subscriptions by Plan</p>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Code</th>
                  <th>Active subscribers</th>
                </tr>
              </thead>
              <tbody>
                {stats.subscriptions.length === 0 && (
                  <tr><td colSpan={3} className="admin-empty">No data</td></tr>
                )}
                {stats.subscriptions.map(s => (
                  <tr key={s.plan_code}>
                    <td>{s.plan_name}</td>
                    <td><span className="admin-badge admin-badge--gray">{s.plan_code}</span></td>
                    <td>{s.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageLayout>
  );
};

export default AdminOverview;
